import React, { useEffect, useMemo, useState } from 'react';
import styles from 'components/nonPodiatry/page.module.scss';
import Button from 'components/common/button';
import { StateType } from 'reducer';
import { NonPodiatryOptionsType } from 'types/types';
import { actionTypesOfNonPodiatry } from 'reducer/actionType';
import InputText from 'components/common/InputText';
import {
  usePersistNonPodiatryReducer,
  ActiveTypeNonPodiatry,
} from 'reducer/nonPodiatry';
import clsx from 'clsx';
import MetaTag from 'components/meta';

const NoneText: string = 'None';
type Props = {
  options: NonPodiatryOptionsType | NonPodiatryOptionsType[];
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function WhatTreatmentsWereHelpful({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  isFilter = false,
}: Props) {
  const [inputText, setInputText] = useState('');
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionList, setQuestionList] = useState<string[]>([NoneText]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [inputTextError, setInputTextError] = useState<boolean>(false);

  const [state, dispatch] = usePersistNonPodiatryReducer();

  const hasNextPage = useMemo(
    () => selectedItems.includes('Physical therapy'),
    [selectedItems],
  );

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: NonPodiatryOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        const treatmentsAtHome = state.find(
          (object: StateType) => object.name === actionTypesOfNonPodiatry.TREATMENTS_TRIED_HOME,
        );
        const treatMentsFromAnotherProvider = state.find(
          (object: StateType) => object.name
            === actionTypesOfNonPodiatry.WHAT_TREATMENTS_RECOMMENDED_PRE_PROVIDER,
        );

        const mergedTreatments = [
          ...treatmentsAtHome.value,
          ...(treatMentsFromAnotherProvider?.value ?? []).filter(
            (item: string) => !treatmentsAtHome.value.includes(item),
          ),
        ];

        if (treatmentsAtHome && treatMentsFromAnotherProvider) {
          setQuestionList((prev) => [...prev, ...mergedTreatments]);
        }
        try {
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionList(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
  }, [actionPage, isFilter, options, state]);

  useEffect(() => {
    if (state) {
      const data = state.find(
        (object: StateType) => object.name === actionPage,
      );
      if (data) {
        setSelectedItems(data.value);
        setInputText(data.text ?? '');
      }
    }
  }, [actionPage, state]);

  const onSelectOptions = (item: string) => {
    if (multiple) {
      if (item === NoneText) {
        setSelectedItems([item]);
      } else {
        setSelectedItems((old) => {
          if (old.toString() === NoneText) {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedItems([item]);
    }

    setError(false);
    setInputTextError(false);
  };

  const clickNextOrSubmit = async () => {
    if (selectedItems.length === 0) {
      setError(true);
      return;
    }

    const dispatchPayload: ActiveTypeNonPodiatry = {
      type: actionPage,
      name: title,
      value: selectedItems,
      text: inputText,
    };
    const other = selectedItems.find((item) => item === 'Other');
    if (other) {
      if (!inputText.trim()) {
        setInputTextError(true);
        return;
      }
    }

    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage, !hasNextPage);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    setInputTextError(false);
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
      <p className="error-message">
        Please select at least one option.
      </p>
      )}

      <div>
        {questionList.map((item) => {
          const isSelected = selectedItems.includes(item);
          return (
            <div key={item}>
              <p
                className={clsx(
                  'option',
                  { 'selected-option': isSelected },
                )}
                onClick={() => onSelectOptions(item)}
                onKeyUp={() => {}}
              >
                {item}
              </p>
              {item === 'Other' && isSelected && (
              <InputText
                value={inputText}
                placeholder="Type..."
                size="small"
                error={inputTextError}
                isWidthFull
                onChange={onChangeText}
                maxLength={160}
              />
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={clickNextOrSubmit}>
          {hasNextPage ? 'Next' : 'Submit'}
        </Button>
      </div>
    </>
  );
}

export default WhatTreatmentsWereHelpful;
